import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

import './AppointmentReminder.css';
import Persona from './components/Persona.jsx';
import TemplateDropdown from './components/TemplateDropdown.jsx';
import GeneratedPrompt from './components/GeneratedPrompt.jsx';
import Alert from "../components/Alert"
import PatientsInformation from './components/PatientsInformation.jsx';
import { templateData } from '../data/templateData';


function AppointmentReminderPersona() {
  const API_URL = process.env.NODE_ENV === 'development' ? "https://demo.aiguardian.io/agent-backend": "/agent-backend";
  const { user, logout } = useAuth();

  const [selectedTemplate, setSelectedTemplate] = useState(''); // 'SMS' or 'Call'
  const [isTemplateConfirmed, setIsTemplateConfirmed] = useState(false);

  const [promptTemplate, setPromptTemplate] = useState('');
  const [showSaveTemplateButton, setShowSaveTemplateButton] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  const [promptPlaceholders, setPromptPlaceholders] = useState([]);
  const [patients, setPatients] = useState([]);

  const [showAlert, setShowAlert] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  
  const triggerAlert = (msg) => {
      setAlertMessage(msg);
      setShowAlert(true);
    };

  const handleSetPromptTemplate = (prompt) => {
    setPromptTemplate(prompt);
    setPromptPlaceholders(extractPlaceholders(prompt));
  }

  const handlePromptChange = (e) => {
    setPromptTemplate(e.target.value);
    setShowSaveTemplateButton(true);
  };

  const handleSaveTemplate = () => {
    setShowSaveTemplateButton(false);
    setShowDialog(false);
  }

  const extractPlaceholders = (promptTemplate) => {
    if (!promptTemplate) return [];

    const placeholderPattern = /<[^<>]+?>/g;
    const matches = promptTemplate.match(placeholderPattern);

    // Remove duplicates by converting to a Set and back to array
    return matches ? Array.from(new Set(matches)) : [];
  };

  // Helper: add months preserving date overflow
  const addMonths = (date, months) => {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    // If month overflowed and day changed, fix to last day of previous month
    if (d.getDate() < day) {
      d.setDate(0);
    }
    return d;
  };

  // Build schedule dates starting from `start` for `years` years according to `frequency`
  const getScheduleDates = (frequency, start = new Date(), years = 10) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(startDate);
    endDate.setFullYear(endDate.getFullYear() + years);

    let cursor = new Date(startDate);
    while (cursor <= endDate) {
      dates.push(new Date(cursor));
      switch (frequency) {
        case 'week':
          cursor = new Date(cursor.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cursor = addMonths(cursor, 1);
          break;
        case '3months':
          cursor = addMonths(cursor, 3);
          break;
        case '6months':
          cursor = addMonths(cursor, 6);
          break;
        case 'year':
          cursor = addMonths(cursor, 12);
          break;
        default:
          // fallback once and break
          cursor = new Date(cursor.getTime() + 7 * 24 * 60 * 60 * 1000);
      }
    }
    return dates;
  };

  // Schedule call tasks for selected patients for the next 10 years
  const handleCallSelectedPatients = async (patients, placeholders, payload = {}) => {
    const { frequency = 'week' } = payload;

    // For each selected patient, create a task for every scheduled date
    const fetchPromises = [];

    patients.filter(p => p.selected).forEach(patient => {
      // Determine schedule start: prefer patient.callTime if it's a valid date string, else now
      let scheduleStart = new Date();
      if (patient.callTime) {
        const parsed = new Date(patient.callTime);
        if (!isNaN(parsed)) scheduleStart = parsed;
      }

      const scheduledDates = getScheduleDates(frequency, scheduleStart, 10);

      scheduledDates.forEach(date => {
        // Create patientPrompt by replacing placeholders
        let patientPrompt = promptTemplate;
        placeholders.forEach(ph => {
          const value = patient[ph] || ""; // Fallback to empty string if undefined
          patientPrompt = patientPrompt.replaceAll(ph, value);
        });

        const newTask = {
          type: "ad-hoc",
          name: patient.name,
          lang: patient.lang,
          phone_number: patient.phone_number,
          prompt: patientPrompt,
          // schedule each occurrence at this ISO timestamp
          call_at: date.toISOString()
        };

        fetchPromises.push(
          fetch(API_URL + '/api/createjob', {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer " + user.token
            },
            body: JSON.stringify(newTask)
          }).then(response => ({ ok: response.ok, name: patient.name }))
            .catch(() => ({ ok: false, name: patient.name }))
        );
      });
    });

    // Run all API calls in parallel (may be many requests depending on frequency)
    const results = await Promise.all(fetchPromises);

    const successCount = results.filter(r => r.ok).length;
    const failCount = results.length - successCount;
    triggerAlert(`Created ${successCount} scheduled calls successfully. ${failCount} failed.`);
  };

  const handleSMSSelectedPatients = async (patients, placeholders, payload = {}) => {
    const { frequency = 'week' } = payload;

    const fetchPromises = [];

    patients.filter(p => p.selected).forEach(patient => {
      let scheduleStart = new Date();
      if (patient.callTime) {
        const parsed = new Date(patient.callTime);
        if (!isNaN(parsed)) scheduleStart = parsed;
      }

      const scheduledDates = getScheduleDates(frequency, scheduleStart, 10);

      scheduledDates.forEach(date => {
        // Create patientPrompt by replacing placeholders
        let patientPrompt = promptTemplate;
        placeholders.forEach(ph => {
          const value = patient[ph] || "";
          patientPrompt = patientPrompt.replaceAll(ph, value);
        });

        const newTask = {
          type: "sms",
          name: patient.name,
          lang: patient.lang,
          phone_number: patient.phone_number,
          message: patientPrompt,
          call_at: date.toISOString()
        };

        fetchPromises.push(
          fetch(API_URL + '/api/createjob', {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer " + user.token
            },
            body: JSON.stringify(newTask)
          }).then(response => ({ ok: response.ok, name: patient.name }))
            .catch(() => ({ ok: false, name: patient.name }))
        );
      });
    });

    const results = await Promise.all(fetchPromises);
    const successCount = results.filter(r => r.ok).length;
    const failCount = results.length - successCount;
    triggerAlert(`Created ${successCount} scheduled SMS/tasks successfully. ${failCount} failed.`);
  };

  
  return (
    <div className="app-container">
      <div className="left-column">
        <Persona personaData={templateData.find(persona => persona.service === "Appointment Reminder")}/>
      </div>
      <div className="right-column">
        <TemplateDropdown 
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          isTemplateConfirmed={isTemplateConfirmed}
          setIsTemplateConfirmed={setIsTemplateConfirmed}
          handleSetPromptTemplate={handleSetPromptTemplate}
        />

        {promptTemplate && (
          <>
            <GeneratedPrompt 
              showSaveTemplateButton={showSaveTemplateButton}
              showDialog={showDialog}
              setShowDialog={setShowDialog}
              promptTemplate={promptTemplate}
              handlePromptChange={handlePromptChange}
              handleSaveTemplate={handleSaveTemplate}
            />
            <PatientsInformation 
              format={selectedTemplate}
              patients={patients}
              setPatients={setPatients}
              placeholders={promptPlaceholders}
              handleCallSelectedPatients={handleCallSelectedPatients}
              handleSMSSelectedPatients={handleSMSSelectedPatients}
            />
          </>
        )}
      </div>

       <Alert
                    show={showAlert}
                    onClose={() => setShowAlert(false)}
                    message={alertMessage}
                  />
                  
    </div>
  );
}

export default AppointmentReminderPersona;
